import * as React from "react";

import { cn } from "@/lib/chillnn/cn";
import { sanitize } from "@/lib/dompurify";

import { DevelopmentTools } from "@/hooks/use-development-tools";

import { Label } from "./label/label";

import type * as LabelPrimitive from "@radix-ui/react-label";

type FormItemContextValue = {
	id: string;
};

const FormItemContext = React.createContext<FormItemContextValue>({} as FormItemContextValue);

const useFormField = () => {
	const itemContext = React.useContext(FormItemContext);

	const { id } = itemContext;

	return {
		id,
		formItemId: `${id}-form-item`,
		formDescriptionId: `${id}-form-item-description`,
		formErrorMessageId: `${id}-form-item-error-message`,
	};
};

const FormItem = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
	({ className, ...props }, ref) => {
		const id = React.useId();

		return (
			<FormItemContext.Provider value={{ id }}>
				<div ref={ref} className={cn("relative space-y-2", className)} {...props} />
			</FormItemContext.Provider>
		);
	},
);
FormItem.displayName = "FormItem";

const FormLabel = React.forwardRef<
	React.ElementRef<typeof LabelPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(({ className, children, ...props }, ref) => {
	const { formItemId } = useFormField();
	const { consoleErrorOnlyDevMode } = DevelopmentTools();

	const body = children;

	if (!body) {
		return null;
	}

	if (formItemId.includes("undefined")) {
		consoleErrorOnlyDevMode(createConsoleErrorMessage("FormLabel"));
	}

	return (
		<Label
			ref={ref}
			className={cn("text-gray-900 block text-sm font-medium", className)}
			htmlFor={formItemId}
			{...props}
		>
			{body}
		</Label>
	);
});
FormLabel.displayName = "FormLabel";

const FormDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
	({ className, ...props }, ref) => {
		const { formDescriptionId } = useFormField();
		const { consoleErrorOnlyDevMode } = DevelopmentTools();

		if (formDescriptionId.includes("undefined")) {
			consoleErrorOnlyDevMode(createConsoleErrorMessage("FormDescription"));
		}

		return <p ref={ref} id={formDescriptionId} className={cn("text-sm text-zinc-500", className)} {...props} />;
	},
);
FormDescription.displayName = "FormDescription";

/** 未指定は今の浮いた枠。card は枠を次の項目の上まで押し下げる。 */
export type FormErrorLayout = "overlay" | "card";

const FORM_ERROR_LAYOUT_CLASS: Record<FormErrorLayout, string> = {
	overlay:
		"absolute z-30 w-max max-w-fit rounded border border-error-400 bg-white p-2 text-sm font-medium text-error-600",
	card: "mt-2 w-full rounded border border-error-400 bg-white p-2 text-sm font-medium text-error-600",
};

const FormErrorMessage = React.forwardRef<
	HTMLElement,
	React.HTMLAttributes<HTMLElement> & { errorMessage?: string; layout?: FormErrorLayout }
>(({ className, children, errorMessage, layout = "overlay", ...props }, ref) => {
	const { formErrorMessageId } = useFormField();
	const { consoleErrorOnlyDevMode } = DevelopmentTools();

	if (formErrorMessageId.includes("undefined")) {
		consoleErrorOnlyDevMode(createConsoleErrorMessage("FormErrorMessage"));
	}

	const body = errorMessage ?? children;

	if (!body) {
		return null;
	}

	const isStringBody = typeof body === "string";
	// パスワード条件は div を含む HTML。p の中に置くとブラウザが要素を外に出す。
	const Tag = layout === "overlay" ? "p" : "div";

	return (
		<Tag
			ref={ref as React.Ref<HTMLParagraphElement & HTMLDivElement>}
			id={formErrorMessageId}
			data-error-layout={layout}
			className={cn(FORM_ERROR_LAYOUT_CLASS[layout], className)}
			{...props}
			{...(isStringBody ? { dangerouslySetInnerHTML: { __html: sanitize(body) } } : { children: body })}
		/>
	);
});
FormErrorMessage.displayName = "FormErrorMessage";

export { FormDescription, FormErrorMessage, FormItem, FormLabel, useFormField };

const createConsoleErrorMessage = (displayName: string) => {
	return `chillnnエラー: FormItem が見つかりませんでした。 <${displayName} /> が <FormItem /> で囲まれているか確認してください。`;
};
